import { Actor, AuthId, CalendarId, type Option } from "@tento-lona/sheets";
import { nonnull } from "@tento-core/assert";
import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { Style, css } from "@tento-ui/component-styles";
import { AuthScopedCalendarId, CalendarsClient } from "@tento-lona";
import { Network } from "@tento-lona/network";
import type { AuthScopedCalendarDisplay, EventsClient } from "@tento-lona";
import {
  CalendarListView,
  CalendarRowView,
  CalendarSwatch,
  CalendarVisibilityIcon,
} from "@tento-ui/components/calendar-list/calendar-list-view";
import { LonaColors } from "@tento-lona/internal-ui";
import { EventAccess } from "../../../../sheet/gestures/event-access.ts";

// Data adapters over the pure @tento-ui calendar-list view
// (@tento-ui/components/calendar-list/calendar-list-view). These map calendar
// data — colour via LonaColors, visibility via CalendarsClient, the Network row
// shape — onto the view's simple props, so the app sidebar/picker/onboarding and
// the GTM feature-card asset all render the same components. Public API
// (UserCalendarList / CalendarRowVisibilityIcon / OtherCalendarList /
// CalendarListRow) is unchanged.

// A calendar's colour as a CSS token string (e.g. "var(--blue)"), or the neutral
// fallback when it has no colour id.
function calendarColor(cal: Network.CalendarDisplay): string {
  const color = cal.colorId
    ? LonaColors.fromColorId(Number.parseInt(cal.colorId))
    : null;
  return color ? `var(--${color})` : "var(--fg-icon-ter)";
}

// The per-calendar visibility toggle (the colour-bordered checkbox). Adapter over
// the pure CalendarVisibilityIcon: derives colour + initial visible state from the
// calendar/CalendarsClient, and routes the toggle back through onChange (default:
// persist via CalendarsClient.setVisible). Usable as a CalendarRowAccessoryFactory.
/**
 * A calendar row's visibility toggle, colored for the calendar and persisted
 * through the `CalendarsClient`.
 */
export namespace CalendarRowVisibilityIcon {
  export function makeWith(
    acal: AuthScopedCalendarDisplay,
    calendars: CalendarsClient,
    onChange: (acid: AuthScopedCalendarDisplay, visible: boolean) => void = (
      acal: AuthScopedCalendarDisplay,
      visible,
    ) => calendars.setVisible(AuthScopedCalendarId.of(acal), visible, Actor.EXTERNAL),
  ): CalendarVisibilityIcon {
    const [_, cal] = acal;
    return CalendarVisibilityIcon.makeWith({
      color: calendarColor(cal),
      checked: calendars.isVisible(AuthScopedCalendarId.of(acal)),
      onToggle: (visible) => onChange(acal, visible),
    });
  }
}

/**
 * Interface for factory functions that create calendar row accessory elements
 */
export type CalendarRowAccessoryFactory = (
  acal: AuthScopedCalendarDisplay,
  calendars: CalendarsClient,
) => HTMLElement;

// Build the accessory element for one calendar: a caller-supplied factory when
// both it and a CalendarsClient are present, else the default solid colour square.
function accessoryFor(
  acal: AuthScopedCalendarDisplay,
  accessoryFactory: Option<CalendarRowAccessoryFactory>,
  calendars: Option<CalendarsClient>,
): HTMLElement {
  if (accessoryFactory && calendars) {
    return accessoryFactory(acal, calendars);
  }
  return CalendarSwatch.makeWith({ color: calendarColor(acal[1]) });
}

// Map one calendar to a pure view row.
function toRow(
  acal: AuthScopedCalendarDisplay,
  options: {
    accessoryFactory: Option<CalendarRowAccessoryFactory>;
    calendars: Option<CalendarsClient>;
    selected: Option<AuthScopedCalendarId>;
    onSelect: Option<(acal: AuthScopedCalendarDisplay) => void>;
  },
): CalendarListView.Row {
  const [aid, cal] = acal;
  const onSelect = options.onSelect;
  const selectedCal = options.selected;
  const selected = selectedCal != null &&
    selectedCal[0] === aid &&
    selectedCal[1] === cal.id;
  return {
    name: cal.summary ?? cal.id,
    $accessory: accessoryFor(acal, options.accessoryFactory, options.calendars),
    selected,
    selectable: onSelect != null,
    onSelect: onSelect ? () => onSelect(acal) : null,
  };
}

// Access-role precedence for row ordering within a section: owner first, then
// writer, reader, freeBusyReader; anything else sorts last.
const ACCESS_ROLE_RANK: Record<string, number> = {
  owner: 0,
  writer: 1,
  reader: 2,
  freeBusyReader: 3,
};

function accessRoleRank(role: string): number {
  return ACCESS_ROLE_RANK[role] ?? Number.MAX_SAFE_INTEGER;
}

// The showEditableOnly filter keeps calendars the user can write to.
function isEditableRole(role: string): boolean {
  return role === "owner" || role === "writer";
}

// Row order within a section: the account's own primary calendar
// (id === authId) first, then by access-role rank, then alphabetically.
function compareCalendars(
  a: Network.CalendarDisplay,
  b: Network.CalendarDisplay,
  authId: string,
): number {
  const aPrimary = a.id === authId ? 0 : 1;
  const bPrimary = b.id === authId ? 0 : 1;
  if (aPrimary !== bPrimary) return aPrimary - bPrimary;
  const rankDiff = accessRoleRank(a.accessRole) - accessRoleRank(b.accessRole);
  if (rankDiff !== 0) return rankDiff;
  return (a.summary ?? a.id).localeCompare(b.summary ?? b.id);
}

/**
 * The calendars of `ucal` the list offers: all of them, or with
 * `showEditableOnly` those whose role can write and that take a new event
 * by `writability`'s answer (`EventAccess.Create.accepts`: an unknown answer is
 * offered, and a write there is refused when it is made).
 */
export function offeredCalendars(
  ucal: Network.CalendarList,
  options: Option<Pick<UserCalendarList.Options, "showEditableOnly" | "writability">>,
): Network.CalendarDisplay[] {
  if (!(options?.showEditableOnly ?? false)) return [...ucal.calendars];
  const writability = options?.writability ?? null;
  return ucal.calendars.filter((cal) =>
    isEditableRole(cal.accessRole) && EventAccess.Create.accepts(writability?.([AuthId.of(ucal.authId), cal]) ?? null)
  );
}


/** The list of the user's own calendars, with the selected one marked. */
export namespace UserCalendarList {
  export type Options = {
    selected?: AuthScopedCalendarId;
    accessoryFactory?: CalendarRowAccessoryFactory;
    calendars?: CalendarsClient;
    onSelect?: (acal: AuthScopedCalendarDisplay) => void;
    padding?: string;
    showEditableOnly?: boolean;
    /** With `showEditableOnly`, also leaves out a calendar this answers is read-only (`EventsClient.peekCalendarWritability`). */
    writability?: (calendar: AuthScopedCalendarDisplay) => Option<EventsClient.Writability>;
    // App-injected hook mapping a calendar source ("google"/"outlook") to a
    // monochrome icon URL for non-primary section headers. This file is
    // sheets-ui and MUST NOT import the app's CalendarProviders directly.
    providerIcon?: (source: string) => Option<string>;
  };

  // One account section. The user's primary Google account is split like
  // Google's own UI — owner calendars under "My calendars", the rest under
  // "Other calendars" (`subOrder` keeps that pair in place) — and both carry
  // an alternate header (the account identity) toggled by clicking.
  type Section = {
    header: CalendarListView.Header;
    altHeader: Option<CalendarListView.Header>;
    authId: string;
    source: string;
    primary: boolean;
    subOrder: number;
    rows: CalendarListView.Row[];
  };

  export function makeWith(
    ucals: readonly Network.CalendarList[],
    options: Option<UserCalendarList.Options>,
  ): HTMLElement {
    const rowOptions = {
      accessoryFactory: options?.accessoryFactory ?? null,
      calendars: options?.calendars ?? null,
      selected: options?.selected ?? null,
      onSelect: options?.onSelect ?? null,
    };
    const providerIcon = options?.providerIcon ?? null;
    const sections: Section[] = [];

    for (const ucal of ucals) {
      // Partition this account's calendars by source — google and outlook can
      // share one authId, so a single CalendarList group can span both.
      const bySource = new Map<string, Network.CalendarDisplay[]>();
      for (const cal of offeredCalendars(ucal, options)) {
        const cals = bySource.get(cal.source) ?? [];
        cals.push(cal);
        bySource.set(cal.source, cals);
      }

      for (const [source, cals] of bySource) {
        if (cals.length === 0) continue;
        const primary = ucal.isPrimary && source === "google";
        cals.sort((a, b) => compareCalendars(a, b, ucal.authId));
        const icon = providerIcon?.(source) ?? null;
        const toRows = (list: Network.CalendarDisplay[]) =>
          list.map((cal) => toRow([ucal.authId, cal], rowOptions));

        if (primary) {
          // Google's own split: owner calendars vs subscribed/shared ones.
          // Each header clicks through to the account identity form.
          const owned = cals.filter((cal) => cal.accessRole === "owner");
          const other = cals.filter((cal) => cal.accessRole !== "owner");
          if (owned.length > 0) {
            sections.push({
              header: { title: "My calendars", subtitle: null, icon: null },
              altHeader: { title: ucal.authId, subtitle: "My calendars", icon },
              authId: ucal.authId,
              source,
              primary,
              subOrder: 0,
              rows: toRows(owned),
            });
          }
          if (other.length > 0) {
            sections.push({
              header: { title: "Other calendars", subtitle: null, icon: null },
              altHeader: {
                title: ucal.authId,
                subtitle: "Other calendars",
                icon,
              },
              authId: ucal.authId,
              source,
              primary,
              subOrder: 1,
              rows: toRows(other),
            });
          }
          continue;
        }

        sections.push({
          header: { title: ucal.authId, subtitle: null, icon },
          altHeader: null,
          authId: ucal.authId,
          source,
          primary,
          subOrder: 0,
          rows: toRows(cals),
        });
      }
    }

    // Primary account's pair first (My calendars, then its Other calendars),
    // then remaining sections by authId alphabetically, then by source (so
    // "google" precedes "outlook" within a shared account).
    sections.sort((a, b) => {
      if (a.primary !== b.primary) return a.primary ? -1 : 1;
      if (a.subOrder !== b.subOrder) return a.subOrder - b.subOrder;
      const byAuth = a.authId.localeCompare(b.authId);
      if (byAuth !== 0) return byAuth;
      return a.source.localeCompare(b.source);
    });

    const groups: CalendarListView.Group[] = sections.map((section) => ({
      header: section.header,
      altHeader: section.altHeader,
      rows: section.rows,
    }));

    return CalendarListView.makeWith({
      groups,
      padding: options?.padding ?? "8px",
    });
  }
}

/** The list of calendars from other accounts. */
export namespace OtherCalendarList {
  export type Options = {
    accessoryFactory?: CalendarRowAccessoryFactory;
    calendars?: CalendarsClient;
    selected?: AuthScopedCalendarId;
    onSelect?: (acal: AuthScopedCalendarDisplay) => void;
  };

  export function makeWith(
    acals: AuthScopedCalendarDisplay[],
    options: Option<OtherCalendarList.Options>,
  ): HTMLElement {
    const rowOptions = {
      accessoryFactory: options?.accessoryFactory ?? null,
      calendars: options?.calendars ?? null,
      selected: options?.selected ?? null,
      onSelect: options?.onSelect ?? null,
    };
    return CalendarListView.makeWith({
      groups: [
        {
          header: { title: "Other calendars", subtitle: null, icon: null },
          altHeader: null,
          rows: acals.map((acal) => toRow(acal, rowOptions)),
        },
      ],
      padding: "0px",
    });
  }
}

export namespace AuthCalendarList {
  export type Options = {
    customLabel?: Option<string>;
    accessoryFactory?: CalendarRowAccessoryFactory;
    calendars?: CalendarsClient;
    selected?: AuthScopedCalendarId;
    onSelect?: (acal: AuthScopedCalendarDisplay) => void;
    showOnlyOwner?: boolean;
  };

  export function makeWith(
    ucal: Network.CalendarList,
    options?: Option<AuthCalendarList.Options>,
  ): HTMLElement {
    const rowOptions = {
      accessoryFactory: options?.accessoryFactory ?? null,
      calendars: options?.calendars ?? null,
      selected: options?.selected ?? null,
      onSelect: options?.onSelect ?? null,
    };
    const calendars = options?.showOnlyOwner
      ? ucal.calendars.filter((cal) => cal.accessRole === "owner")
      : ucal.calendars;
    return CalendarListView.makeWith({
      groups: [
        {
          header: {
            title: options?.customLabel ?? ucal.authId,
            subtitle: null,
            icon: null,
          },
          altHeader: null,
          rows: calendars.map((cal) => toRow([ucal.authId, cal], rowOptions)),
        },
      ],
      padding: "0px",
    });
  }
}

// A single calendar row bound to calendar data — kept for the event-details
// "Calendar" field (event-fields.ts), which holds one and re-binds it. Adapter
// over the pure CalendarRowView: sets the auth/calendar ids (for calendarId())
// and forwards --cell-height to the inner row.
@component({
  name: "lona-calendar-list-row",
})
export class CalendarListRow extends LonaWebComponent {
  #$row: CalendarRowView = this.hydrate("row");

  static makeWith(
    acal: AuthScopedCalendarDisplay,
    accessoryFactory?: Option<CalendarRowAccessoryFactory>,
    calendars?: Option<CalendarsClient>,
    onSelect?: Option<(acal: AuthScopedCalendarDisplay) => void>,
  ): CalendarListRow {
    const $e = CalendarListRow.make();
    $e.slot = "calendar-row";
    $e.bind(acal, accessoryFactory, calendars, onSelect);
    return $e;
  }

  calendarId(): AuthScopedCalendarId {
    return nonnull(
      AuthScopedCalendarId.parse(
        nonnull(this.getAttribute("auth-id")),
        nonnull(this.getAttribute("calendar-id")),
      ),
      "calendar row: empty auth-id / calendar-id",
    );
  }

  bind(
    acal: AuthScopedCalendarDisplay,
    accessoryFactory?: Option<CalendarRowAccessoryFactory>,
    calendars?: Option<CalendarsClient>,
    onSelect?: Option<(acal: AuthScopedCalendarDisplay) => void>,
  ): this {
    const [aid, cal] = acal;
    this.setAttribute("auth-id", aid);
    this.setAttribute("calendar-id", cal.id);

    this.#$row.bind({
      name: cal.summary ?? cal.id,
      $accessory: accessoryFor(
        acal,
        accessoryFactory ?? null,
        calendars ?? null,
      ),
      selected: false,
      selectable: onSelect != null,
      onSelect: onSelect ? () => onSelect(acal) : null,
    });
    return this;
  }

  static $styles: Style.Compat[] = [
    css`
      :host {
        display: block;
        width: 100%;
      }
    `,
  ];
  static $html: Option<HTMLTemplateElement> = html`
    <std-calendar-list-row id="row"></std-calendar-list-row>
  `;
}
